import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ClipboardList, Loader2, Search, Truck, X } from "lucide-react";
import { api, fmt, apiError } from "@/lib/api";

const STAGES = [
  { id: "", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "picking", label: "Picking" },
  { id: "packed", label: "Packed" },
  { id: "ready_to_dispatch", label: "Ready to Dispatch" },
  { id: "in_transit", label: "In Transit" },
  { id: "out_for_delivery", label: "Out for Delivery" },
  { id: "failed_delivery", label: "Failed" },
  { id: "delivered", label: "Delivered" },
  { id: "cancelled", label: "Cancelled" },
];
const STAGE_META = {
  confirmed: ["Confirmed", "bg-sky-500/15 text-sky-300 border-sky-500/30"],
  picking: ["Picking", "bg-violet-500/15 text-violet-300 border-violet-500/30"],
  packed: ["Packed", "bg-indigo-500/15 text-indigo-300 border-indigo-500/30"],
  ready_to_dispatch: ["Ready to Dispatch", "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"],
  dispatched: ["Dispatched", "bg-blue-500/15 text-blue-300 border-blue-500/30"],
  in_transit: ["In Transit", "bg-blue-500/15 text-blue-300 border-blue-500/30"],
  out_for_delivery: ["Out for Delivery", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  failed_delivery: ["Failed Delivery", "bg-red-500/15 text-red-300 border-red-500/30"],
  delivered: ["Delivered", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"],
  cancelled: ["Cancelled", "bg-white/[0.06] text-neutral-500 border-white/10"],
};
const NEXT = {
  confirmed: ["picking"],
  picking: ["packed", "confirmed"],
  packed: ["ready_to_dispatch", "picking"],
  ready_to_dispatch: ["dispatched", "packed"],
  dispatched: ["in_transit", "out_for_delivery"],
  in_transit: ["out_for_delivery"],
  out_for_delivery: ["delivered", "failed_delivery"],
  failed_delivery: ["out_for_delivery"],
  delivered: [],
  cancelled: [],
};
const PARTNERS = [
  { id: "self_fleet", label: "Self fleet" }, { id: "porter", label: "Porter" },
  { id: "dunzo", label: "Dunzo" }, { id: "delhivery", label: "Delhivery" },
  { id: "bluedart", label: "BlueDart" }, { id: "dtdc", label: "DTDC" }, { id: "other", label: "Other" },
];
const KPIS = [
  ["pending_fulfillment", "Pending Fulfillment", ""], ["picking", "Picking", "picking"],
  ["packed", "Packed", "packed"], ["ready_to_dispatch", "Ready to Dispatch", "ready_to_dispatch"],
  ["in_transit", "In Transit", "in_transit"], ["out_for_delivery", "Out for Delivery", "out_for_delivery"],
  ["delivered", "Delivered", "delivered"], ["failed_delivery", "Failed Delivery", "failed_delivery"],
];
const PAY_META = {
  paid: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  cod_pending: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  credit_pending: "border-violet-500/30 bg-violet-500/10 text-violet-300",
};
const input = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

function StageChip({ status }) {
  const [label, cls] = STAGE_META[status] || [status, "bg-white/[0.06] text-neutral-400 border-white/10"];
  return <span data-testid={`stage-${status}`} className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-bold ${cls}`}>{label}</span>;
}

function ActionModal({ order, action, onDone, onClose }) {
  const isDispatch = action === "dispatched";
  const isFailed = action === "failed_delivery";
  const isPacked = action === "packed";
  const [note, setNote] = useState("");
  const [partner, setPartner] = useState("self_fleet");
  const [awb, setAwb] = useState("");
  const [when, setWhen] = useState(new Date().toISOString().slice(0, 16));
  const [reason, setReason] = useState("");
  const [packed, setPacked] = useState(() => Object.fromEntries(order.items.map((i) => [i.sku, i.qty])));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (isFailed) {
        await api.post(`/admin/fulfillment/orders/${order.id}/failed-delivery`, { reason });
      } else {
        const payload = { to: action, note };
        if (isDispatch) Object.assign(payload, { partner, awb, dispatched_at: when ? new Date(when).toISOString() : "" });
        if (isPacked) payload.items = order.items.map((i) => ({ sku: i.sku, qty: Number(packed[i.sku] ?? i.qty) }));
        await api.post(`/admin/fulfillment/orders/${order.id}/transition`, payload);
      }
      toast.success(`${order.order_number} → ${STAGE_META[action][0]}`);
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Transition failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="fulfill-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#F1EDE3] p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-mono text-lg font-bold text-amber-400">{order.order_number}</h3>
            <p className="text-xs text-neutral-500">Move to <strong className="text-white">{STAGE_META[action][0]}</strong></p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="fulfill-modal-close"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          {isPacked && order.items.map((i) => (
            <div key={i.sku} className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-300">{i.name} <span className="text-neutral-600">({i.sku})</span></p>
              <span className="flex items-center gap-2 text-xs text-neutral-500">
                packed
                <input type="number" min="0" max={i.qty} value={packed[i.sku]} data-testid={`packed-qty-${i.sku}`}
                  onChange={(e) => setPacked((p) => ({ ...p, [i.sku]: e.target.value }))}
                  className="h-9 w-20 rounded-lg border border-white/10 bg-black/40 px-2 text-sm text-white text-center focus:border-amber-500/60 focus:outline-none" />
                of {i.qty}
              </span>
            </div>
          ))}
          {isDispatch && (
            <>
              <select value={partner} onChange={(e) => setPartner(e.target.value)} data-testid="dispatch-partner" className={input}>
                {PARTNERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <input value={awb} onChange={(e) => setAwb(e.target.value)} data-testid="dispatch-awb"
                placeholder={partner === "self_fleet" ? "Reference (optional for self fleet)" : "AWB / tracking number (required)"} className={input} />
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Dispatch date & time</label>
                <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="dispatch-at" className={input} />
              </div>
            </>
          )}
          {isFailed && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="failed-reason"
              placeholder="Reason (e.g. customer not available, address issue)" className={input} />
          )}
          {!isFailed && (
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="fulfill-note"
              placeholder={isPacked ? "Package details / note (optional)" : "Note (optional)"} className={input} />
          )}
          <button onClick={submit} disabled={busy || (isFailed && reason.trim().length < 3)} data-testid="fulfill-submit"
            className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-black transition-colors disabled:opacity-40 ${isFailed ? "bg-red-500 hover:bg-red-400" : "bg-amber-500 hover:bg-amber-400"}`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {isFailed ? "Record failed attempt" : `Move to ${STAGE_META[action][0]}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderDetail({ order }) {
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    api.get(`/admin/fulfillment/orders/${order.id}/activity`)
      .then(({ data }) => setActivity(data.events))
      .catch(() => setActivity([]));
  }, [order.id]);
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Items to pick / pack</p>
        <div className="space-y-1.5" data-testid="pick-items">
          {order.items.map((i) => (
            <div key={i.sku} className="flex items-center justify-between text-xs">
              <span className="text-neutral-300">{i.name} <span className="text-neutral-600">({i.sku})</span></span>
              <span className="text-neutral-400">
                <strong className="text-white">{i.qty}</strong> {i.unit}
                {order.packed_items && order.packed_items[i.sku] !== undefined && (
                  <span className={order.packed_items[i.sku] < i.qty ? "ml-2 text-amber-300" : "ml-2 text-emerald-400"}>
                    packed {order.packed_items[i.sku]}/{i.qty}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
        {order.partial && (
          <p className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-1.5 text-[11px] text-amber-200/80" data-testid="partial-flag">
            Partial fulfillment — some quantities were packed short.
          </p>
        )}
        {order.dispatch && (
          <p className="mt-2 text-[11px] text-neutral-500" data-testid="dispatch-info">
            Dispatch: {order.dispatch.partner.replace(/_/g, " ")}{order.dispatch.awb && ` · AWB ${order.dispatch.awb}`} · {String(order.dispatch.at).slice(0, 16).replace("T", " ")} · by {order.dispatch.by}
          </p>
        )}
        {(order.delivery_attempts || []).length > 0 && (
          <div className="mt-2 space-y-1" data-testid="attempt-history">
            {order.delivery_attempts.map((a) => (
              <p key={a.at} className="text-[11px] text-red-300/80">
                Attempt {a.n} failed · {String(a.at).slice(0, 16).replace("T", " ")} · {a.reason} · by {a.by}
              </p>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Activity timeline</p>
        {!activity ? (
          <Loader2 className="h-4 w-4 animate-spin text-neutral-600" />
        ) : (
          <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1" data-testid="activity-timeline" data-lenis-prevent>
            {[...activity].reverse().map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-[11px]">
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${e.kind === "failed_attempt" ? "bg-red-400" : e.kind === "order_placed" ? "bg-sky-400" : "bg-amber-400"}`} />
                <div>
                  <p className="text-neutral-300">{e.label}{e.note ? <span className="text-neutral-500"> — {e.note}</span> : null}</p>
                  <p className="text-neutral-600">{String(e.at).slice(0, 16).replace("T", " ")} · {e.by}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminFulfillment() {
  const [kpis, setKpis] = useState(null);
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("pending");
  const [q, setQ] = useState("");
  const [payment, setPayment] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [action, setAction] = useState(null); // {order, to}

  const load = useCallback(() => {
    api.get("/admin/fulfillment/kpis").then(({ data }) => setKpis(data)).catch(() => {});
    api.get("/admin/fulfillment/orders", { params: { stage, q, payment } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load fulfillment orders")));
  }, [stage, q, payment]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-fulfillment" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><Truck className="h-6 w-6 text-amber-400" /> Fulfillment</h1>
        <p className="mt-1 text-sm text-neutral-500">Confirmed → Picking → Packed → Ready to Dispatch → Dispatched → Out for Delivery → Delivered. Cancellations stay in Admin → Orders (they restock inventory).</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="fulfillment-kpis">
        {KPIS.map(([key, label, stageId]) => (
          <button key={key} onClick={() => setStage(stageId)} data-testid={`kpi-${key}`}
            className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold text-neutral-300 transition-colors hover:border-amber-500/40 hover:text-amber-300">
            {label}: <span className="text-white">{kpis ? kpis[key] : "…"}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.07] pb-px" data-testid="stage-tabs">
        {STAGES.map((s) => (
          <button key={s.id} onClick={() => setStage(s.id)} data-testid={`stage-tab-${s.id || "all"}`}
            className={`rounded-t-xl px-3.5 py-2.5 text-xs font-bold transition-colors ${stage === s.id ? "bg-white/[0.05] text-amber-300 border-b-2 border-amber-500" : "text-neutral-500 hover:text-neutral-300"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order, customer, tracking…" data-testid="fulfillment-search"
            className="h-10 w-72 rounded-xl border border-white/10 bg-black/40 pl-10 pr-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        </div>
        <select value={payment} onChange={(e) => setPayment(e.target.value)} data-testid="payment-filter"
          className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none">
          <option value="">Payment: all</option>
          <option value="paid">Paid</option>
          <option value="cod_pending">COD pending</option>
          <option value="credit_pending">Credit</option>
          <option value="pending">Online pending</option>
        </select>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : data.orders.length === 0 ? (
        <div data-testid="fulfillment-empty" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500">
          <ClipboardList className="mx-auto mb-3 h-7 w-7 text-neutral-600" />
          No orders in this stage.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07]">
          <table className="w-full min-w-[980px] text-left text-sm" data-testid="fulfillment-table">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="w-8 px-3 py-3"></th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Items</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Delivery</th>
                <th className="px-4 py-3">Tracking</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => (
                <FragmentRow key={o.id} order={o} expanded={expanded === o.id}
                  onToggle={() => setExpanded(expanded === o.id ? null : o.id)}
                  onAction={(to) => setAction({ order: o, to })} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {action && (
        <ActionModal order={action.order} action={action.to}
          onDone={() => { setAction(null); load(); }} onClose={() => setAction(null)} />
      )}
    </div>
  );
}

function FragmentRow({ order: o, expanded, onToggle, onAction }) {
  return (
    <>
      <tr className="border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`fulfillment-row-${o.order_number}`}>
        <td className="px-3 py-3">
          <button onClick={onToggle} aria-label="Details" data-testid={`fulfillment-expand-${o.order_number}`}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white">
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </td>
        <td className="px-4 py-3">
          <p className="font-mono text-xs font-bold text-amber-400">{o.order_number}</p>
          <p className="text-[10px] text-neutral-600">{(o.created_at || "").slice(0, 10)}</p>
        </td>
        <td className="px-4 py-3">
          <p className="text-xs font-semibold text-white">{o.customer}</p>
          {o.business && <p className="text-[10px] text-neutral-500">{o.business}</p>}
        </td>
        <td className="px-4 py-3 text-xs text-neutral-400">{o.items.length} line{o.items.length > 1 ? "s" : ""} · {o.units} units
          {o.partial && <span className="ml-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold text-amber-300">PARTIAL</span>}
        </td>
        <td className="px-4 py-3">
          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold ${PAY_META[o.payment_status] || "border-white/10 bg-white/[0.04] text-neutral-400"}`}>
            {o.payment_status === "cod_pending" ? "COD" : o.payment_status === "credit_pending" ? "Credit" : o.payment_status}
          </span>
        </td>
        <td className="px-4 py-3 text-xs capitalize text-neutral-400">{o.shipping_method}</td>
        <td className="px-4 py-3 font-mono text-[11px] text-neutral-500">{o.tracking_id || "—"}</td>
        <td className="px-4 py-3"><StageChip status={o.status} /></td>
        <td className="px-4 py-3 text-right">
          <span className="flex flex-wrap items-center justify-end gap-1.5">
            {(NEXT[o.status] || []).map((to) => (
              <button key={to} onClick={() => onAction(to)} data-testid={`action-${to}-${o.order_number}`}
                className={`h-8 rounded-full border px-3 text-[11px] font-bold transition-colors ${to === "failed_delivery" ? "border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20" : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"}`}>
                {to === "failed_delivery" ? "Failed attempt" : `→ ${STAGE_META[to][0]}`}
              </button>
            ))}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/[0.05] bg-black/30">
          <td colSpan={9} className="px-6 py-4"><OrderDetail order={o} /></td>
        </tr>
      )}
    </>
  );
}
